import { AssociatesNotFoundContainer, AssociatesAndMapContainer, AssociatesContent, BackgroundContainer, ButtonContainer, Container, FormContainer, InputContainer, MapContainer, ErrorMessage, MapSkeletonWrapper, Header, SubTitle } from "./styles";
import { OutlinedInput } from "../../../../components/OutlinedInput";
import React, { useEffect, useState, Suspense } from "react";
import { SolidButton } from "../../../../components/SolidButton";
import AssociatesItem from "./components/AssociatesItem";
import { showToast } from "../../../../components/CustomToast";
import { i18n } from "../../../../localization/i18n";
import { Skeleton } from "@mui/material";

import * as Yup from "yup";
import { typesErrors } from "../../../../utils/validateFormFields/yupErrors";
import api from "../../../../services/api";
import { maskCEP, maskPhone, onlyNumbers, capitalizeName } from "../../../../utils/formatters";
import { validateCEP, validatePhone } from "../../../../utils/validators";

const GoogleMap = React.lazy(() => import("./components/GoogleMap"));

interface IAssociatesProps {
  id: string;
  name: string;
  phone: string;
  instagram: string;
  profile_picture: string;
  professional_number: string;
  distance: string;
  duration: string;
  rating: number;
  latLong: {
    lat: number;
    lng: number;
    text: string;
  }
}

interface ILeadLocation {
  lat: number,
  lng: number
}

export default function FindNearestFixiter() {
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapRenderData, setMapRenderData] = useState<{
    licensees: IAssociatesProps[];
    fixiters: IAssociatesProps[];
    readyToRenderMap: boolean;
  }>({ licensees: [], fixiters: [], readyToRenderMap: false });

  const [name, setName] = useState('')
  const [cep, setCep] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [nearbyLicensees, setNearbyLicensees] = useState<IAssociatesProps[]>([])
  const [nearbyFixiters, setNearbyFixiters] = useState<IAssociatesProps[]>([])
  const [centerCoordinates, setCenterCoordinates] = useState({ lat: 0, lng: 0 })
  const [blockedAfterSearch, setBlockedAfterSearch] = useState(false)
  const [leadLocation, setLeadLocation] = useState<ILeadLocation>()
  const [backendError, setBackendError] = useState<string | null>(null)

  /*

  const [valueIndex, setValueIndex] = useState(0);
  const referenceValues = [10, 25, 50, 100, 200, 500];
  const denormalizeValue = (index: number) => referenceValues[index];
  const selectedValue = denormalizeValue(valueIndex);
  const marks =  referenceValues.map((value, index) => ({
    value: index,
    label: `${String(value)} km`,
  }))


  const [distanceRange, setDistanceRange] = useState(10)
  const [distanceOptions, setDistanceOptions] = useState({
    options: [
 r    { value: 1, label: '10 km' },
      { value: 2, label: '25 km' },
      { value: 3, label: '50 km' },
      { value: 4, label: '100 km' },
      { value: 5, label: '200 km' },
    ],
    max: 5
  })

  */


  async function handleSearchAssociates() {
    setLoading(true)
    setBackendError(null)
    const body = {
      name: capitalizeName(name),
      phone_number: onlyNumbers(phoneNumber),
      cep: onlyNumbers(cep),
      coordinates: leadLocation
    }

    const yup_validation = Yup.object().shape({
      name: Yup.string()
        .required('Nome incompleto ou inválido')
        .test('is-full-name', 'Nome incompleto ou inválido', (value) => {
          if (!value) return false;
          const words = value.trim().split(/\s+/);
          return words.length >= 2;
        }),
      phone_number: Yup.string()
        .required('Telefone inválido ou incompleto')
        .test('is-phone-valid', 'Telefone inválido ou incompleto', (value) => validatePhone(value || '')),
      cep: Yup.string()
        .required('CEP inválido ou incompleto')
        .test('is-cep-valid', 'CEP inválido ou incompleto', (value) => validateCEP(value || ''))
    })

    try {
      await yup_validation.validate(body, { abortEarly: false });
    } catch (err: any) {
      setLoading(false);
      
      const formErrors: any = {};
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path && !formErrors[error.path]) {
            formErrors[error.path] = error.message;
          }
        });
      }
      
      setErrors(formErrors);
      showToast({
        type: "error",
        message: i18n.t("form_errors.alert"),
      });

      return;
    }

    setErrors({});

      try {
      let foundAssociates = false;
      await api.post('patients-leads/find-nearby-fixiters', body).then((response) => {
        console.log(response.data)
        
        const licensees = response.data.nearbyLicensees || []
        const fixiters = response.data.nearbyFixiters || []
        
        // Passar os dados para os cards EXATAMENTE AGORA (Pintura imediata no DOM)
        setNearbyLicensees(licensees)
        setNearbyFixiters(fixiters)
        setCenterCoordinates(response.data.center)
        localStorage.setItem('@FixitApp:Lead', response.data.lead.id)
        
        if (licensees.length > 0 || fixiters.length > 0) {
          foundAssociates = true;
          setMapLoading(true);
        }
      })

      // Libera a tela. O Mapa ainda NÃO sabe que tem que renderizar.
      setLoading(false)
      
      if (foundAssociates) {
        setBlockedAfterSearch(true)
      } else {
        setBlockedAfterSearch(false)
        setLeadLocation(undefined)
      }

    } catch (error: any) {
      const apiMessage = error.response?.data?.message || i18n.t("Não foi possivel buscar os associados mais próximos de você. Tente novamente mais tarde.");
      setBackendError(apiMessage);
      
      showToast({
        type: "error",
        message: apiMessage,
      });
      setLoading(false)
      setBlockedAfterSearch(false)
    }

  }

  useEffect(() => {
    // 1. Carrega o mapa em background de forma assíncrona, sem bloquear a UI principal
    import("./components/GoogleMap");

    // 2. Atrasa a chamada de GPS para garantir que o navegador desenhou os inputs primeiro
    if ("geolocation" in navigator) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          navigator.geolocation.getCurrentPosition((position) => {
            setLeadLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            })
          })
        });
      } else {
        // Fallback for Safari which doesn't support requestIdleCallback
        setTimeout(() => {
          navigator.geolocation.getCurrentPosition((position) => {
            setLeadLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            })
          })
        }, 1000);
      }
    }
  }, [])

  // 3. Efeito Cascata: Assim que o DOM sinalizar que os cards foram injetados, liberamos o mapa
  useEffect(() => {
    if (nearbyLicensees.length > 0 || nearbyFixiters.length > 0) {
      setMapRenderData({
        licensees: nearbyLicensees,
        fixiters: nearbyFixiters,
        readyToRenderMap: true
      });
    } else {
      setMapRenderData(prev => ({...prev, readyToRenderMap: false}));
    }
  }, [nearbyLicensees, nearbyFixiters])


  /*

   useEffect(() => {
     if (window.innerWidth <= 1000) {
       setDistanceOptions({
         options: [
           { value: 10, label: '10 km' },
           { value: 25, label: '25 km' },
           { value: 50, label: '50 km' },
           { value: 100, label: '100 km' },
         ],
         max: 5,
       })
     } else {
       setDistanceOptions({
         options: [
           { value: 1, label: '10 km' },
           { value: 2, label: '25 km' },
           { value: 3, label: '50 km' },
           { value: 4, label: '100 km' },
           { value: 5, label: '200 km' },
         ],
         max: 5,
       })
     }
   }, [window.innerWidth])

   */



  return (
    <BackgroundContainer>
      <Container>
        <Header>Encontre um credenciado Fix it mais próximo de você.</Header>
        <SubTitle>Preencha com seus dados para prosseguir.</SubTitle>
        <FormContainer onSubmit={(event) => {
          event.preventDefault()
          handleSearchAssociates()
        }}>
          <InputContainer>
            <OutlinedInput 
              readOnly={loading} 
              error={errors?.cep} 
              handleChange={(event) => {
                setCep(maskCEP(event));
                if (errors.cep) setErrors({ ...errors, cep: undefined });
              }} 
              inputName={"cep"} 
              label={"CEP"} 
              value={cep} 
            />
            <OutlinedInput 
              readOnly={loading} 
              error={errors?.name} 
              handleChange={(event) => {
                setName(event);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }} 
              inputName={"name"} 
              label={"Nome completo"} 
              value={name} 
            />
            <OutlinedInput 
              readOnly={loading} 
              error={errors?.phone_number} 
              handleChange={(event) => {
                setPhoneNumber(maskPhone(event));
                if (errors.phone_number) setErrors({ ...errors, phone_number: undefined });
              }} 
              inputName={"phoneNumber"} 
              label={"Telefone (WhatsApp)"} 
              value={phoneNumber} 
            />
          </InputContainer>

          {/*
          <SliderContainer>
            <Slider
              aria-label="Distância"
              valueLabelDisplay="on"
              valueLabelFormat={(index) => denormalizeValue(index)}
              step={1}
              min={0}
              max={referenceValues.length-1}
              value={valueIndex}
              marks={marks}
              color="secondary"
              onChange={(event, newValue) => setValueIndex(newValue as number)}
            />
          </SliderContainer>
          */}

          <ButtonContainer>
            <SolidButton disabled={blockedAfterSearch || loading} type="submit" text={loading ? "Buscando..." : "Buscar"} buttonWidth={200} />
          </ButtonContainer>

          {backendError && (
            <ErrorMessage>{backendError}</ErrorMessage>
          )}

          {loading && (
            <AssociatesAndMapContainer>
              <MapContainer>
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
              </MapContainer>

              <AssociatesContent>
                <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 2, marginBottom: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 2, marginBottom: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 2 }} />
              </AssociatesContent>
            </AssociatesAndMapContainer>
          )}

          {!loading && (nearbyLicensees.length > 0 || nearbyFixiters.length > 0) && (
            <AssociatesAndMapContainer>

              <MapContainer>
                <Suspense fallback={
                  <MapSkeletonWrapper>
                    <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
                  </MapSkeletonWrapper>
                }>
                  {mapRenderData.readyToRenderMap && (
                    <GoogleMap 
                      onLoad={() => setMapLoading(false)}
                      associatesData={[...mapRenderData.licensees, ...mapRenderData.fixiters]} referential={
                      {
                        center: centerCoordinates,
                        zoom: 7,
                      }
                    } />
                  )}
                </Suspense>
              </MapContainer>

              <AssociatesContent>
                {nearbyLicensees.map((associate: any) =>
                  <AssociatesItem
                    key={associate.id}
                    id={associate.id}
                    name={associate.name}
                    phone={associate.phone}
                    instagram={associate.instagram}
                    profile_picture={associate.profile_picture}
                    professional_number={associate.professional_number}
                    expertise={associate.expertise}
                    city={associate.city}
                    state={associate.state}
                    distance={associate.distance}
                    duration={associate.duration}
                    rating={associate.rating}
                    isPremium={true} />
                )}
                
                {nearbyFixiters.map((associate: any) =>
                  <AssociatesItem
                    key={associate.id}
                    id={associate.id}
                    name={associate.name}
                    phone={associate.phone}
                    instagram={associate.instagram}
                    profile_picture={associate.profile_picture}
                    professional_number={associate.professional_number}
                    expertise={associate.expertise}
                    city={associate.city}
                    state={associate.state}
                    distance={associate.distance}
                    duration={associate.duration}
                    rating={associate.rating}
                    isPremium={false} />
                )}
              </AssociatesContent>

            </AssociatesAndMapContainer>
          )}
          {!loading && centerCoordinates.lat !== 0 && centerCoordinates.lng !== 0 && nearbyLicensees.length === 0 && nearbyFixiters.length === 0 && (
            <AssociatesNotFoundContainer>
              <p>{`Não encontramos nenhum licenciado em um raio de 500km da sua localização.`}</p>
            </AssociatesNotFoundContainer>
          )
          }
        </FormContainer>


      </Container>

    </BackgroundContainer>
  )
}
